import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, auth, setCurrentSession, type AuthSession } from '@/data'
import type { Membership } from '@/domain/vacations'

const ORG_KEY = 'nolens-vacations.org'

interface SessionValue {
  /** undefined while the stored session is being read. */
  session: AuthSession | null | undefined
  memberships: Membership[] | undefined
  membershipsLoading: boolean
  membership: Membership | null
  /** True after the user clicked "Terminar sessão" (no "return to where you were"). */
  signedOut: boolean
  switchOrg: (orgId: string) => void
  signOut: () => Promise<void>
  refreshMemberships: () => Promise<unknown>
}

const Ctx = createContext<SessionValue | null>(null)

function readOrg() {
  try {
    return localStorage.getItem(ORG_KEY)
  } catch {
    return null
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const [session, setSession] = useState<AuthSession | null | undefined>(undefined)
  const [orgId, setOrgId] = useState<string | null>(readOrg)
  const [signedOut, setSignedOut] = useState(false)

  useEffect(() => {
    let alive = true
    void auth.getSession().then((s) => {
      if (!alive) return
      setCurrentSession(s)
      setSession(s)
    })
    const off = auth.onChange((s) => {
      setCurrentSession(s)
      setSession(s)
      if (s) setSignedOut(false)
      else qc.clear()
    })
    return () => {
      alive = false
      off()
    }
  }, [qc])

  const userKey = session?.user.email ?? null
  const memberships = useQuery({
    queryKey: ['memberships', userKey],
    queryFn: () => api.memberships(),
    enabled: Boolean(userKey),
  })

  const membership = useMemo(() => {
    const list = memberships.data ?? []
    return list.find((m) => m.organization.id === orgId) ?? (list.length === 1 ? list[0]! : null)
  }, [memberships.data, orgId])

  const switchOrg = useCallback((id: string) => {
    try {
      localStorage.setItem(ORG_KEY, id)
    } catch {
      // ignore
    }
    setOrgId(id)
  }, [])

  const value: SessionValue = {
    session,
    memberships: memberships.data,
    membershipsLoading: memberships.isLoading,
    membership,
    signedOut,
    switchOrg,
    signOut: async () => {
      setSignedOut(true)
      await auth.signOut()
      qc.clear()
    },
    refreshMemberships: () => memberships.refetch(),
  }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useSession() {
  const v = useContext(Ctx)
  if (!v) throw new Error('useSession fora do SessionProvider')
  return v
}
